const LdJsonVideo = ({ content, url, summary, thumbnailUrl, authors, sections, sectionUrl }) => {

    const domain = process.env.NEXT_PUBLIC_DOMAIN_URL || 'https://www.ellitoral.com';
    const absoluteUrl = url?.startsWith('http') ? url : `${domain}${url}`;

    const {
        name,
        description,
        publishedDate,
        modificatedAt,
        videoFiles,
        entityId,
        duration,
        tags
    } = content;

    const stripHtml = (html = '') => {
        return html
            .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
            .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
            .replace(/<[^>]+>/g, ' ')
            .replace(/&nbsp;/gi, ' ')
            .replace(/&amp;/gi, '&')
            .replace(/&quot;/gi, '"')
            .replace(/&#39;/gi, "'")
            .replace(/&lt;/gi, '<')
            .replace(/&gt;/gi, '>')
            .replace(/\s+/g, ' ')
            .trim();
    };

    const parseDuration = (dur = '') => {
        const parts = dur.split(':').map(Number);
        if (parts.length === 2) {
            const [m, s] = parts;
            return `PT${m > 0 ? m + 'M' : ''}${s}S`;
        }
        if (parts.length === 3) {
            const [h, m, s] = parts;
            return `PT${h > 0 ? h + 'H' : ''}${m > 0 ? m + 'M' : ''}${s}S`;
        }
        return undefined;
    };

    const videoDescription = stripHtml(description) || summary || name;
    const videoUrl = videoFiles?.mp4 || videoFiles?.m3u8 || '';
    const jwPlayerId = process.env.NEXT_PUBLIC_LOOP_JWPLAYER_ID?.split('/libraries/')[1]?.replace('.js', '');
    const jwPlayerThumbnail = entityId ? `https://cdn.jwplayer.com/v2/media/${entityId}/poster.jpg?width=720` : '';
    const defaultPlaceholder = 'https://img-ellitoral.lilax.io/collections/utiles-diseno/generica-redes-1___6273ea8aae16950017079e88.jpg';
    const rawThumbnail = thumbnailUrl || jwPlayerThumbnail || defaultPlaceholder;
    const videoThumbnail = rawThumbnail.replace(/width=\d+/, 'width=720');
    const embedUrl = entityId && jwPlayerId ? `https://cdn.jwplayer.com/players/${entityId}-${jwPlayerId}.html` : null;

    let data = [];

    const LdVideo = {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        "name": `[Video] ${name}`,
        "description": videoDescription,
        "thumbnailUrl": [videoThumbnail],
        "uploadDate": publishedDate,
        "contentUrl": videoUrl,
        "url": absoluteUrl,
        "inLanguage": "es-AR",
        "isAccessibleForFree": true,
        "publisher": {
            "@type": "Organization",
            "name": "El Litoral",
            "url": "https://www.ellitoral.com",
            "logo": {
                "@type": "ImageObject",
                "url": `${domain}/img/header_logoellitoral.svg`,
                "width": 439,
                "height": 60
            }
        },
        "copyrightHolder": { "@type": "Organization", "name": "El Litoral" },
        "copyrightYear": publishedDate ? publishedDate.split('-')[0] : new Date().getFullYear().toString()
    };

    if (embedUrl) LdVideo.embedUrl = embedUrl;

    const isoDuration = parseDuration(duration);
    if (isoDuration) LdVideo.duration = isoDuration;

    if (authors && authors.length > 0) {
        LdVideo.author = authors.map(author => ({
            "@type": "Person",
            "name": author.name,
            "url": 'https://www.ellitoral.com/autor/' + author.slug,
            "sameAs": 'https://www.ellitoral.com/autor/' + author.slug,
            ...(author.imageUrl && { "image": author.imageUrl })
        }));
    }

    if (tags && tags.length > 0) {
        LdVideo.keywords = tags.map(t => t.name).join(', ');
        LdVideo.about = tags.map(t => ({ "@type": "Thing", "name": t.name }));
    }

    data.push(LdVideo);

    const LdArticle = {
        "@context": "https://schema.org",
        "@type": "NewsArticle",
        "mainEntityOfPage": { "@type": "WebPage", "@id": absoluteUrl },
        "headline": `[Video] ${name}`,
        "url": absoluteUrl,
        "description": videoDescription,
        "image": [{
            "@type": "ImageObject",
            "url": videoThumbnail,
            "width": 720,
            "datePublished": publishedDate,
            "dateModified": modificatedAt
        }],
        "dateCreated": publishedDate,
        "datePublished": publishedDate,
        "dateModified": modificatedAt,
        "isAccessibleForFree": true,
        "citation": { "@type": "CreativeWork", "url": absoluteUrl },
        "articleSection": ["Videos", ...(sections ? sections.map(s => s.name) : [])],
        "author": (authors && authors.length > 0)
            ? authors.map(author => ({
                "@type": "Person",
                "name": author.name,
                "url": 'https://www.ellitoral.com/autor/' + author.slug,
                "sameAs": 'https://www.ellitoral.com/autor/' + author.slug
            }))
            : [{ "@type": "Organization", "name": "El Litoral", "url": "https://www.ellitoral.com" }],
        "publisher": {
            "@type": "Organization",
            "name": "El Litoral",
            "url": "https://www.ellitoral.com",
            "logo": {
                "@type": "ImageObject",
                "url": `${domain}/img/header_logoellitoral.svg`,
                "width": 439,
                "height": 60
            },
            "sameAs": ["https://www.facebook.com/ellitoralcom", "https://instagram.com/ellitoral", "https://twitter.com/ellitoral", "https://www.youtube.com/c/EllitoralArgentina"]
        },
        "isPartOf": {
            "@type": ["CreativeWork", "Product"],
            "name": "El Litoral",
            "url": "https://www.ellitoral.com"
        },
        "hasPart": {
            "@type": "WebPageElement",
            "isAccessibleForFree": true,
            "cssSelector": ".article"
        },
        "video": {
            "@type": "VideoObject",
            "name": `[Video] ${name}`,
            "description": videoDescription,
            "thumbnailUrl": videoThumbnail,
            "uploadDate": publishedDate,
            "contentUrl": videoUrl
        }
    };

    if (embedUrl) LdArticle.video.embedUrl = embedUrl;
    if (isoDuration) LdArticle.video.duration = isoDuration;
    if (tags && tags.length > 0) LdArticle.keywords = tags.map(t => t.name).join(', ');

    data.push(LdArticle);

    const breadcrumbItems = [
        { "@type": "ListItem", "position": 1, "name": "El Litoral", "item": domain },
        { "@type": "ListItem", "position": 2, "name": "VIDEOS", "item": `${domain}/videos` }
    ];
    if (sections && sections.length > 0) {
        sections.forEach((section, index) => {
            breadcrumbItems.push({
                "@type": "ListItem",
                "position": index + 3,
                "name": section.name?.toUpperCase(),
                "item": `${domain}${sectionUrl}`
            });
        });
    }
    data.push({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbItems
    });

    data.push({
        "@context": "https://schema.org",
        "@type": "Organization",
        "legalName": "El Litoral",
        "name": "El Litoral",
        "description": "Diario El Litoral, el medio de comunicación de mayor trayectoria de la provincia de Santa Fe. Noticias de Santa Fe, Argentina y el mundo actualizadas las 24 horas.",
        "url": "https://www.ellitoral.com",
        "logo": `${domain}/img/header_logoellitoral.svg`,
        "foundingDate": "1918",
        "address": {
            "@type": "PostalAddress",
            "streetAddress": "Elizabeth Guillamón de Agnellini 2670",
            "addressLocality": "Santa Fe",
            "addressRegion": "Santa Fe",
            "postalCode": "3000",
            "addressCountry": "AR"
        },
        "sameAs": ["https://www.facebook.com/ellitoralcom", "https://instagram.com/ellitoral", "https://twitter.com/ellitoral", "https://www.youtube.com/c/EllitoralArgentina"]
    });

    data.push({
        "@context": "https://schema.org",
        "@type": "NewsMediaOrganization",
        "name": "El Litoral",
        "description": "Diario El Litoral, el medio de comunicación de mayor trayectoria de la provincia de Santa Fe. Noticias de Santa Fe, Argentina y el mundo actualizadas las 24 horas.",
        "url": "https://www.ellitoral.com",
        "masthead": "https://www.ellitoral.com/quienes-somos",
        "publishingPrinciples": "https://www.ellitoral.com/quienes-somos",
        "ethicsPolicy": "https://www.ellitoral.com/politicas-privacidad-facebook-news",
        "correctionsPolicy": "https://www.ellitoral.com/politicas-privacidad-facebook-news",
        "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+543426305344",
            "contactType": "customer service",
            "email": "contenidos@ellitoral.com"
        }
    });

    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
        />
    );
};

export default LdJsonVideo;
